import React, { useCallback, useEffect, useRef } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";

/**
 * The shell a dealer record panel opens in: the dimmed backdrop, a header
 * that stays in view while the panel scrolls, one optional attention line
 * under it, then the body - the main column with a sidebar beside it when
 * there is one (stacked on narrow screens).
 *
 * The backdrop, the × and Escape all close it through `canClose`, so a panel
 * with unsaved text can ask first (return false to stay open). Escape only
 * closes the panel on top when another one is open over it.
 *
 * Props: label (for screen readers), onClose(), canClose() -> bool, header,
 * attention, tabs, sidebar, children
 */
const PanelFrame = ({ label, onClose, canClose, header, attention, tabs, sidebar, children }) => {
  const dialogRef = useRef(null);

  const requestClose = useCallback(() => {
    if (canClose && !canClose()) return;
    onClose();
  }, [canClose, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs[dialogs.length - 1] === dialogRef.current) requestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  return (
    <div className={`${classes.modal} ${classes.overNav}`} onClick={requestClose}>
      <div
        ref={dialogRef}
        className={`${classes.modalContent} ${classes.widePanel} ${classes.stackedForm} ${styles.panel}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={label}
      >
        <div className={`${styles.stickyHeader} ${styles.frameHeader} ${tabs ? "" : styles.stickyHeaderPlain}`}>
          <div className={styles.headerRow}>
            <div className={styles.headerMain}>{header}</div>
            <button type="button" className={styles.closeButton} onClick={requestClose} aria-label="Close">×</button>
          </div>
          {tabs}
        </div>
        {attention}
        {sidebar ? (
          <div className={styles.panelBody}>
            <div className={styles.panelMain}>{children}</div>
            <aside className={styles.panelSide}>{sidebar}</aside>
          </div>
        ) : children}
      </div>
    </div>
  );
};

export default PanelFrame;
